import React, { useState, useEffect, useCallback } from 'react';
import {
    IconButton,
    Badge,
    Menu,
    Box,
    Typography,
    Button,
    Divider,
    CircularProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import MailIcon from '@mui/icons-material/Mail';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import AddTaskIcon from '@mui/icons-material/AddTask';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../config';
import EmailReviewModal from './EmailReviewModal';

const DARK_BG      = '#1a1d2e';
const DARK_SURFACE = '#242740';
const DARK_BORDER  = 'rgba(255,255,255,0.10)';
const ACCENT       = '#6C63FF';
const TEXT_PRIMARY = '#f0f0f5';
const TEXT_MUTED   = 'rgba(240,240,245,0.55)';

const formatReceivedDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const MessageCenter = ({ isLoggedIn }) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [pendingShares, setPendingShares] = useState([]);
    const [userMessages, setUserMessages] = useState([]);
    const [pendingCount, setPendingCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [responding, setResponding] = useState(null);
    const [emailDialogMsg, setEmailDialogMsg] = useState(null);
    const [reviewMsg, setReviewMsg] = useState(null);
    const [trips, setTrips] = useState([]);

    const fetchTrips = useCallback(async () => {
        if (!isLoggedIn) return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/trips/get.php?status=active`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setTrips(Array.isArray(data) ? data : (data.trips || []));
            }
        } catch (err) {
            console.error('Failed to fetch trips:', err);
        }
    }, [isLoggedIn]);

    const fetchPendingShares = useCallback(async () => {
        if (!isLoggedIn) return;
        
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/pending_shares.php`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setPendingShares(data.pending_shares || []);
                setUserMessages(data.user_messages || []);
                setPendingCount(data.pending_count || 0);
            }
        } catch (err) {
            console.error('Failed to fetch pending shares:', err);
        }
    }, [isLoggedIn]);

    // Fetch on mount and poll every 30 seconds
    useEffect(() => {
        fetchPendingShares();
        const interval = setInterval(fetchPendingShares, 30000);
        return () => clearInterval(interval);
    }, [fetchPendingShares]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        setLoading(true);
        fetchTrips();
        fetchPendingShares().finally(() => setLoading(false));
    };

    // Called by the review modal after an item is placed; refresh so the
    // warning state and unread count stay in sync.
    const handleReviewResolved = useCallback(() => {
        fetchPendingShares();
    }, [fetchPendingShares]);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleRespondToShare = async (sharedTripId, action) => {
        setResponding(sharedTripId);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/respond_share.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ shared_trip_id: sharedTripId, action })
            });
            if (response.ok) {
                setPendingShares(prev => prev.filter(s => s.shared_trip_id !== sharedTripId));
                setPendingCount(prev => Math.max(0, prev - 1));
            }
        } catch (err) {
            console.error('Failed to respond to share:', err);
        } finally {
            setResponding(null);
        }
    };

    const handleDismissMessage = async (messageId) => {
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_BASE_URL}/dismiss_message.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ message_id: messageId })
            });
            // Mark as read locally — keep it in the list
            setUserMessages(prev => prev.map(m => m.id === messageId ? { ...m, is_read: 1 } : m));
            setPendingCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Failed to dismiss message:', err);
        }
    };

    const allItems = [
        ...pendingShares.map(s => ({ ...s, _kind: 'share' })),
        ...userMessages.map(m => ({ ...m, _kind: 'email' }))
    ];

    if (!isLoggedIn) return null;

    return (
        <>
            <IconButton
                onClick={handleClick}
                sx={{
                    p: 0.5,
                    borderRadius: 2,
                    bgcolor: 'transparent',
                    color: 'rgba(255,255,255,0.88)',
                    '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.1)'
                    }
                }}
                title={t('messages')}
            >
                <Badge 
                    badgeContent={pendingCount} 
                    color="error"
                    sx={{
                        '& .MuiBadge-badge': {
                            fontSize: '0.65rem',
                            minWidth: '16px',
                            height: '16px',
                            padding: '0 4px'
                        }
                    }}
                >
                    <MailIcon sx={{ fontSize: { xs: 22, sm: 24 }, color: 'rgba(255,255,255,0.88)' }} />
                </Badge>
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                    style: { backgroundColor: DARK_BG, color: TEXT_PRIMARY, border: `1px solid ${DARK_BORDER}`, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' },
                    sx: { width: 320, maxHeight: 460, mt: 1 }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <Box style={{ backgroundColor: DARK_SURFACE }} sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${DARK_BORDER}` }}>
                    <Typography variant="subtitle1" fontWeight="bold" style={{ color: TEXT_PRIMARY }}>
                        Messages
                    </Typography>
                    <Typography variant="caption" style={{ color: TEXT_MUTED }}>
                        Send itinerary &amp; ticket emails to{' '}
                        <span style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>info@agentriplanner.com</span>
                    </Typography>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={24} sx={{ color: ACCENT }} />
                    </Box>
                ) : allItems.length === 0 ? (
                    <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                        <Typography variant="body2" style={{ color: TEXT_MUTED }}>
                            No new messages
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {allItems.map((item, index) => (
                            <Box key={item._kind === 'share' ? `share-${item.shared_trip_id}` : `msg-${item.id}`}>
                                {index > 0 && <Divider style={{ borderColor: DARK_BORDER }} />}

                                {item._kind === 'share' ? (
                                    <Box sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom style={{ color: TEXT_PRIMARY }}>
                                            Trip Share Request
                                        </Typography>
                                        <Typography variant="body2" gutterBottom style={{ color: TEXT_MUTED }}>
                                            <strong style={{ color: TEXT_PRIMARY }}>{item.owner_name || item.owner_public_id}</strong> wants to share:
                                        </Typography>
                                        <Typography variant="body2" gutterBottom style={{ color: ACCENT, fontWeight: 600 }}>
                                            {item.trip_name || `Trip ${item.trip_id}`}
                                        </Typography>
                                        <Typography variant="caption" display="block" gutterBottom style={{ color: TEXT_MUTED }}>
                                            Full access to view and edit
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<CheckIcon />}
                                                onClick={() => handleRespondToShare(item.shared_trip_id, 'accept')}
                                                disabled={responding === item.shared_trip_id}
                                                sx={{ flex: 1, bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, color: '#fff', fontWeight: 600 }}
                                            >
                                                Accept
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<CloseIcon />}
                                                onClick={() => handleRespondToShare(item.shared_trip_id, 'reject')}
                                                disabled={responding === item.shared_trip_id}
                                                sx={{ flex: 1, borderColor: 'rgba(242,139,130,0.5)', color: '#f28b82', '&:hover': { borderColor: '#f28b82', bgcolor: 'rgba(242,139,130,0.08)' } }}
                                            >
                                                Reject
                                            </Button>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box sx={{ p: 2, opacity: item.is_read ? 0.55 : 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                            <EmailIcon sx={{ fontSize: 16 }} style={{ color: item.is_read ? TEXT_MUTED : ACCENT }} />
                                            <Typography variant="subtitle2" fontWeight={item.is_read ? 'normal' : 'bold'} sx={{ flex: 1 }} style={{ color: TEXT_PRIMARY }}>
                                                Email Received
                                            </Typography>
                                            {item.meta?.inserted > 0 && (
                                                <Chip
                                                    label={`+${item.meta.inserted} activities`}
                                                    size="small"
                                                    sx={{ fontSize: '0.65rem', height: 18, bgcolor: 'rgba(76,175,80,0.2)', color: '#81c784', border: '1px solid rgba(76,175,80,0.3)' }}
                                                />
                                            )}
                                            {item.meta?.updated > 0 && (
                                                <Chip
                                                    label={`${item.meta.updated} ${item.meta.updated === 1 ? 'change' : 'changes'}`}
                                                    size="small"
                                                    sx={{ fontSize: '0.65rem', height: 18, bgcolor: 'rgba(33,150,243,0.2)', color: '#64b5f6', border: '1px solid rgba(33,150,243,0.35)' }}
                                                />
                                            )}
                                            {item.meta?.needs_review && item.meta?.unplaced?.length > 0 && (
                                                <Chip
                                                    icon={<ReportProblemOutlinedIcon sx={{ fontSize: 14, color: '#ffb74d !important' }} />}
                                                    label={`${item.meta.unplaced.length} not placed`}
                                                    size="small"
                                                    sx={{ fontSize: '0.65rem', height: 18, bgcolor: 'rgba(255,167,38,0.18)', color: '#ffb74d', border: '1px solid rgba(255,167,38,0.35)', '& .MuiChip-icon': { ml: '4px' } }}
                                                />
                                            )}
                                        </Box>
                                        <Typography variant="body2" fontWeight="500" gutterBottom noWrap style={{ color: TEXT_PRIMARY }}>
                                            {item.subject || '(no subject)'}
                                        </Typography>
                                        <Typography variant="caption" display="block" gutterBottom style={{ color: TEXT_MUTED }}>
                                            From: {item.sender_email}
                                        </Typography>
                                        {item.created_at && (
                                            <Typography variant="caption" display="block" gutterBottom style={{ color: TEXT_MUTED }}>
                                                Received: {formatReceivedDate(item.created_at)}
                                            </Typography>
                                        )}
                                        {Array.isArray(item.meta?.changes) && item.meta.changes.length > 0 && (
                                            <Box sx={{ mt: 0.5, mb: 0.5 }}>
                                                {item.meta.changes.map((c, ci) => (
                                                    <Typography key={ci} variant="caption" display="block" style={{ color: '#64b5f6' }}>
                                                        ↻ {c.title}: {c.summary || `${c.from} → ${c.to}`}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        )}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                            {item.meta?.needs_review && item.meta?.unplaced?.length > 0 && (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={<AddTaskIcon sx={{ fontSize: 14 }} />}
                                                    sx={{ fontSize: '0.72rem', textTransform: 'none', bgcolor: '#f9a825', color: '#1a1d2e', fontWeight: 600, '&:hover': { bgcolor: '#f57f17' } }}
                                                    onClick={() => setReviewMsg(item)}
                                                >
                                                    Place manually
                                                </Button>
                                            )}
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<EmailIcon sx={{ fontSize: 14 }} />}
                                                sx={{ fontSize: '0.72rem', textTransform: 'none', borderColor: DARK_BORDER, color: TEXT_PRIMARY, '&:hover': { borderColor: ACCENT, color: ACCENT, bgcolor: `${ACCENT}11` } }}
                                                onClick={() => setEmailDialogMsg(item)}
                                            >
                                                Show email
                                            </Button>
                                            {!item.is_read && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => handleDismissMessage(item.id)}
                                                    sx={{ fontSize: '0.72rem', textTransform: 'none', borderColor: DARK_BORDER, color: TEXT_MUTED, '&:hover': { borderColor: 'rgba(255,255,255,0.3)', color: TEXT_PRIMARY } }}
                                                >
                                                    Dismiss
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}
            </Menu>

            {/* Manual placement modal for emails that couldn't be auto-placed */}
            <EmailReviewModal
                open={Boolean(reviewMsg)}
                message={reviewMsg}
                trips={trips}
                onClose={() => setReviewMsg(null)}
                onResolved={handleReviewResolved}
            />

            {/* Full-screen email viewer dialog */}
            <Dialog
                open={Boolean(emailDialogMsg)}
                onClose={() => setEmailDialogMsg(null)}
                fullScreen
                sx={{ zIndex: 1400 }}
                PaperProps={{ style: { backgroundColor: DARK_BG, color: TEXT_PRIMARY } }}
            >
                <DialogTitle
                    style={{ backgroundColor: DARK_SURFACE, borderBottom: `1px solid ${DARK_BORDER}` }}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1.5 }}
                >
                    <EmailIcon sx={{ flexShrink: 0, color: ACCENT }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" noWrap style={{ color: TEXT_PRIMARY }}>
                            {emailDialogMsg?.subject || '(no subject)'}
                        </Typography>
                        <Typography variant="caption" display="block" style={{ color: TEXT_MUTED }}>
                            From: {emailDialogMsg?.sender_email}
                        </Typography>
                        {emailDialogMsg?.created_at && (
                            <Typography variant="caption" display="block" style={{ color: TEXT_MUTED }}>
                                Received: {formatReceivedDate(emailDialogMsg.created_at)}
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={() => setEmailDialogMsg(null)} size="small" sx={{ color: TEXT_MUTED, '&:hover': { color: TEXT_PRIMARY }, flexShrink: 0 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }} style={{ backgroundColor: DARK_BG }}>
                    <Box
                        sx={{
                            p: 3,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            lineHeight: 1.7,
                        }}
                        style={{ color: TEXT_PRIMARY }}
                    >
                        {emailDialogMsg?.body || ''}
                    </Box>
                </DialogContent>
                <DialogActions style={{ backgroundColor: DARK_SURFACE, borderTop: `1px solid ${DARK_BORDER}` }}>
                    <Button
                        onClick={() => { const m = emailDialogMsg; setEmailDialogMsg(null); setReviewMsg(m); }}
                        variant="outlined"
                        startIcon={<AddTaskIcon />}
                        sx={{ borderColor: '#f9a825', color: '#f9a825', fontWeight: 600, '&:hover': { borderColor: '#f57f17', color: '#f57f17', bgcolor: 'rgba(249,168,37,0.08)' } }}
                    >
                        Add to plan
                    </Button>
                    <Button
                        onClick={() => setEmailDialogMsg(null)}
                        variant="contained"
                        sx={{ bgcolor: ACCENT, color: '#fff', '&:hover': { bgcolor: '#574fd6' }, fontWeight: 600 }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MessageCenter;
